import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, Trophy, Users, Calendar, ChevronRight, Loader2, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import axios from 'axios';
import { getToken } from '../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function parseCompDate(yyyymmdd) {
    if (!yyyymmdd || yyyymmdd.length < 8) return null;
    return new Date(
        parseInt(yyyymmdd.slice(0, 4), 10),
        parseInt(yyyymmdd.slice(4, 6), 10) - 1,
        parseInt(yyyymmdd.slice(6, 8), 10)
    );
}

function formatCompDate(yyyymmdd) {
    const d = parseCompDate(yyyymmdd);
    if (!d) return yyyymmdd;
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

const STATUS_COLORS = {
    open: 'bg-blue-500 text-white',
    in_progress: 'bg-green-500 text-white',
    completed: 'bg-purple-500 text-white',
};

const STATUS_LABELS = {
    open: 'Open',
    in_progress: 'In Progress',
    completed: 'Completed',
};

// ── Hero card for the featured competition ────────────────────────────────────

function FeaturedHero({ comp, onJoin, joining }) {
    const navigate = useNavigate();

    const isLive = comp.status === 'in_progress';
    const isCompleted = comp.status === 'completed';

    return (
        <div className="relative mb-8 rounded-2xl overflow-hidden bg-gradient-to-br from-primary/20 via-primary/10 to-background border border-primary/30">
            {/* Background dot pattern */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                }} />
            </div>

            {/* Hero image — fills card, fades out on the left so text stays readable */}
            {comp.hero_image && (
                <div className="absolute inset-0 pointer-events-none">
                    <img
                        src={comp.hero_image}
                        alt=""
                        className="w-full h-full object-cover"
                        style={{
                            objectPosition: `${comp.hero_image_x ?? 50}% ${comp.hero_image_y ?? 50}%`,
                            opacity: comp.hero_image_opacity ?? 0.6,
                        }}
                    />
                    {/* Gradient: solid dark on the left → transparent on the right */}
                    <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
                </div>
            )}

            <div className="relative p-8 md:p-12">
                {/* Status badge */}
                <div className="flex items-center gap-2 mb-6">
                    {isCompleted && (
                        <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 px-3 py-1">
                            <Trophy className="h-3 w-3 mr-1" />
                            COMPLETED
                        </Badge>
                    )}
                    {isLive && (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30 px-3 py-1 animate-pulse">
                            <Sparkles className="h-3 w-3 mr-1" />
                            LIVE NOW
                        </Badge>
                    )}
                    {!isLive && !isCompleted && (
                        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 px-3 py-1">
                            <Sparkles className="h-3 w-3 mr-1" />
                            FEATURED
                        </Badge>
                    )}
                </div>

                {/* Name */}
                <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-4 uppercase tracking-wide page-title">
                    {comp.name}
                </h1>

                {/* Description */}
                {comp.description && (
                    <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mb-8 leading-relaxed">
                        {comp.description}
                    </p>
                )}

                {/* Meta row */}
                <div className="flex flex-wrap items-center gap-4 mb-8 text-sm text-muted-foreground">
                    {comp.entry_count != null && (
                        <span className="flex items-center gap-1.5">
                            <Users className="h-4 w-4" />
                            {comp.entry_count} {comp.entry_count === 1 ? 'entry' : 'entries'}
                        </span>
                    )}
                    {comp.start_date && (
                        <span className="flex items-center gap-1.5">
                            <Calendar className="h-4 w-4" />
                            {formatCompDate(comp.start_date)}
                        </span>
                    )}
                </div>

                {/* Action button */}
                {comp.is_joined === false ? (
                    <Button
                        size="lg"
                        className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8"
                        onClick={() => onJoin(comp.id)}
                        disabled={joining}
                    >
                        {joining ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Join Competition
                    </Button>
                ) : (
                    <Button
                        size="lg"
                        className="bg-green-600 hover:bg-green-700 text-white font-semibold px-8"
                        onClick={() => navigate(`/football/${comp.id}`)}
                    >
                        Make Predictions
                    </Button>
                )}
            </div>
        </div>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function FootballCompetitionsPage() {
    const [comps, setComps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [joining, setJoining] = useState(false);
    const navigate = useNavigate();

    const token = getToken();

    useEffect(() => {
        const load = async () => {
            try {
                const res = await axios.get(`${API_URL}/api/football/competitions`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setComps(res.data.competitions || []);
            } catch {
                toast.error('Failed to load competitions');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [token]);

    const handleJoin = async (compId) => {
        setJoining(true);
        try {
            await axios.post(
                `${API_URL}/api/football/competitions/${compId}/join`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Joined! Time to make your predictions.');
            setComps(prev =>
                prev.map(c => c.id === compId ? { ...c, is_joined: true } : c)
            );
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to join competition');
        } finally {
            setJoining(false);
        }
    };

    // Pick the first open or in_progress competition as the hero
    const featured = comps.find(c => c.status === 'open' || c.status === 'in_progress') || null;
    const rest = comps.filter(c => c !== featured);

    if (loading) {
        return (
            <div className="container-app py-6 space-y-6">
                <Skeleton className="h-72 w-full rounded-2xl" />
                <div className="space-y-3">
                    {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
                </div>
            </div>
        );
    }

    return (
        <div className="container-app py-6 space-y-6">
            {/* Featured hero */}
            {featured ? (
                <FeaturedHero comp={featured} onJoin={handleJoin} joining={joining} />
            ) : comps.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                        <Trophy className="h-8 w-8 mx-auto mb-3 opacity-30" />
                        <p>No competitions available right now.</p>
                    </CardContent>
                </Card>
            ) : null}

            {/* Remaining competitions list */}
            {rest.length > 0 && (
                <div className="space-y-3">
                    {featured && (
                        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                            Other Competitions
                        </h2>
                    )}
                    {rest.map(comp => (
                        <Card
                            key={comp.id}
                            className="hover:border-primary/50 transition-colors cursor-pointer"
                            onClick={() => navigate(`/football/${comp.id}`)}
                        >
                            <CardContent className="p-4 flex items-center gap-4">
                                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                    <Globe className="h-5 w-5 text-primary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-sm truncate">{comp.name}</p>
                                    {comp.description && (
                                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                                            {comp.description}
                                        </p>
                                    )}
                                    <div className="flex items-center gap-2 mt-1.5">
                                        <Badge className={`text-xs ${STATUS_COLORS[comp.status] || 'bg-slate-500 text-white'}`}>
                                            {STATUS_LABELS[comp.status] || comp.status}
                                        </Badge>
                                        {comp.entry_count != null && (
                                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                <Users className="h-3 w-3" />
                                                {comp.entry_count} {comp.entry_count === 1 ? 'entry' : 'entries'}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
